import { useInfiniteQuery, type UseInfiniteQueryResult } from 'react-query';
import { useMemo } from 'react';
import { TableParams } from '../../types/common';
import {
  TradeOrderService,
  Wealthlane_AwardSettlementService_Dtos_TradeOrder_PlaceOrderDashboardDto,
} from '../../services/wealthlane-award-settlement-services';
import moment from 'moment';
import { DATE_FORMAT } from '../../constants/common';

// Define the response type based on the API documentation
interface PaginatedPlaceOrderDashboardDto {
  hasMoreItems?: boolean;
  items?: Array<Wealthlane_AwardSettlementService_Dtos_TradeOrder_PlaceOrderDashboardDto> | null;
  totalCount?: number; // May be available from the API even if not in type
}

// Extended return type with calculated totalCount
type UseGetTradeOrderPlaceOrdersDashboardV2Return = UseInfiniteQueryResult<
  PaginatedPlaceOrderDashboardDto | undefined,
  unknown
> & {
  totalCount: number;
};

const useGetTradeOrderPlaceOrdersDashboardV2 = (
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>,
  tableInfo: TableParams
): UseGetTradeOrderPlaceOrdersDashboardV2Return => {
  const { companyIds, filter, sorting, orderStatusId, cancelStatusId, dateTime, _force } =
    tableInfo;

  // Always use 25 as max result count
  const maxResultCount = 25;

  const getTradeOrderPlaceOrdersV2 = useInfiniteQuery(
    [
      'getTradeOrderPlaceOrdersDashboardV2',
      orderStatusId,
      cancelStatusId,
      dateTime, // Include dateTime in query key
      companyIds,
      filter,
      maxResultCount,
      sorting,
      _force,
    ],
    async ({ pageParam }) => {
      // pageParam will be { skipCount, dateTime } or undefined for first page
      const skipCount = pageParam?.skipCount || 0;
      const nextDateTime = pageParam?.dateTime || dateTime;

      const response = await handleRequest(
        TradeOrderService.getApiAwardSettlementApiV2TradeOrderPlacedOrderDashboard(
          orderStatusId ? [orderStatusId] : undefined,
          cancelStatusId ? [cancelStatusId] : undefined,
          nextDateTime, // Use dateTime from pageParam or initial dateTime
          companyIds,
          maxResultCount,
          sorting,
          undefined
        )
      );
      return response as PaginatedPlaceOrderDashboardDto | undefined;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        // Check if lastPage exists and has items
        if (!lastPage || !lastPage.items) return undefined;

        // Check if items array is empty
        if (lastPage.items.length === 0) return undefined;

        // Primary check: Use hasMoreItems flag from API
        if (lastPage.hasMoreItems === false) {
          return undefined;
        }

        // Secondary check: If we have totalCount, use it for precise control
        if (lastPage.totalCount !== undefined) {
          const totalFetched = allPages.reduce((sum, page) => sum + (page?.items?.length || 0), 0);

          if (totalFetched >= lastPage.totalCount) {
            return undefined;
          }
        }

        // Calculate next skipCount based on total items fetched
        const totalFetched = allPages.reduce((sum, page) => sum + (page?.items?.length || 0), 0);

        // Get the last date from the last item in the last page
        const lastItem = lastPage.items[lastPage.items.length - 1];
        const lastDate = lastItem?.createdDate;

        // Return both skipCount and dateTime for next page
        return {
          skipCount: totalFetched,
          dateTime: lastDate, // Pass the last date to fetch data from that date onwards
        };
      },
      select: (data) => {
        if (!data) return undefined;

        return {
          pages: data.pages.map((page) => ({
            ...page,
            items: page?.items?.map(
              (item: Wealthlane_AwardSettlementService_Dtos_TradeOrder_PlaceOrderDashboardDto) => ({
                ...item,
                createdDate: moment(item.createdDate).format(DATE_FORMAT),
              })
            ),
          })),
          pageParams: data.pageParams,
        } as any;
      },
    }
  );

  // Calculate total count from all loaded pages
  const totalCount = useMemo(() => {
    if (!getTradeOrderPlaceOrdersV2.data?.pages) return 0;

    // First, try to use API's totalCount if available
    const apiTotal = getTradeOrderPlaceOrdersV2.data.pages[0]?.totalCount;
    if (apiTotal !== undefined && apiTotal !== null) {
      return apiTotal;
    }

    // Otherwise, calculate from loaded items
    const loadedCount = getTradeOrderPlaceOrdersV2.data.pages.reduce((total, page) => {
      return total + (page?.items?.length || 0);
    }, 0);

    // If there are more pages, add 1 to show load more button
    // If no more pages, use loaded count
    return getTradeOrderPlaceOrdersV2.hasNextPage ? loadedCount + 1 : loadedCount;
  }, [getTradeOrderPlaceOrdersV2.data, getTradeOrderPlaceOrdersV2.hasNextPage]);

  return {
    ...getTradeOrderPlaceOrdersV2,
    totalCount,
  };
};

export default useGetTradeOrderPlaceOrdersDashboardV2;
